import { useT } from '@/i18n';
import { formatMoney, inputToMinor, moneyToInput } from '@/utils/money';
import { useIsWideViewport } from '@/utils/useMediaQuery';
import type { TableSession } from '@restaurant/api-client';
import {
  Block,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useState } from 'react';
import { useTableSession, useTakePayment } from '../hooks/useTableOrdering';

/**
 * Step 2 of settling - take a payment. Two modes from one page:
 *  - Unsplit tab: pick a method, fill the amount (pre-seeded to the outstanding
 *    balance) and an optional tip, record it.
 *  - Collecting a split share (`?paymentId=`): the amount is fixed to that
 *    share's planned total - you only choose the method and an optional tip.
 * Either way it returns to the bill hub, where the next share or the close
 * step is one tap away.
 */
export default function BillPaymentPage({
  f7route,
}: {
  f7route: { params: { sessionId: string }; query: { paymentId?: string } };
}) {
  const tAll = useT();
  const t = tAll.tableOrdering.payment;
  const sessionId = f7route.params.sessionId;
  const paymentId = f7route.query.paymentId;
  const { data: session, isLoading } = useTableSession(sessionId);

  return (
    <Page name="bill-payment">
      <Navbar title={paymentId ? t.collect : t.takePayment} backLink={tAll.back} />
      {isLoading || !session ? (
        <Block style={{ textAlign: 'center', paddingTop: 48 }}>
          <Preloader size={32} />
        </Block>
      ) : (
        <PaymentForm session={session} paymentId={paymentId} />
      )}
    </Page>
  );
}

function PaymentForm({ session, paymentId }: { session: TableSession; paymentId?: string }) {
  const tAll = useT();
  const t = tAll.tableOrdering.payment;
  const tBill = tAll.tableOrdering.bill;
  const takePayment = useTakePayment();
  const isWide = useIsWideViewport();
  const currency = session.currency;
  const balance = Math.max(0, session.totalCents - session.paidCents);

  // Settling is a sub-task launched from the table; when the payment closes the
  // tab there's nothing left on the bill, so pop the whole /bill subtree back to
  // the surface it was launched from rather than stranding the operator on a
  // read-only receipt. That entry differs by layout - the floor (docked
  // inspector) on wide, the session page on phone - both already in history.
  const exitToSession = () =>
    f7.views.current.router.back(
      isWide ? '/tables' : `/tables/session/${session.id}`,
      // `force` is what pops every /bill page back to that in-history url -
      // a plain back(url) only relabels the immediately-previous page.
      { force: true },
    );

  // Share mode: the payment to settle is fixed, so is its amount.
  const shareIndex = paymentId ? session.payments.findIndex((p) => p.id === paymentId) : -1;
  const share = shareIndex >= 0 ? session.payments[shareIndex] : undefined;
  const dueCents = share ? share.amountCents : balance;

  // Staff collect cash/manual here; gift cards are redeemed via their own action.
  const [payMethod, setPayMethod] = useState<'CASH' | 'MANUAL' | 'ONLINE'>('CASH');
  const [payAmount, setPayAmount] = useState(() => moneyToInput(balance, currency));
  const [tip, setTip] = useState('');

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  function submitPayment() {
    // For a share the planned amount is authoritative; otherwise use the typed
    // amount. The tip is taken in both modes.
    const amountCents = share ? share.amountCents : inputToMinor(payAmount, currency);
    if (amountCents <= 0) return toast(t.enterAmount);
    takePayment.mutate(
      {
        sessionId: session.id,
        body: {
          methodKind: payMethod,
          amountCents,
          tipCents: inputToMinor(tip, currency),
          ...(share ? { paymentId: share.id } : {}),
        },
      },
      {
        onSuccess: (updated) => {
          toast(share ? t.shareCollected : t.paymentRecorded);
          // This payment settled the tab (server auto-closes on Σ paid ≥ total)
          // → leave the bill entirely. Otherwise more is owed (a mid-split share
          // or an underpayment) → step back into the flow to continue.
          if (updated.status === 'CLOSED') exitToSession();
          else f7.views.current.router.back();
        },
      },
    );
  }

  return (
    <div className="to-bill">
      <Block className="to-bill-lead">
        <span>{share ? t.shareOf(shareIndex + 1, session.payments.length) : tBill.balanceDue}</span>
        <b>{formatMoney(dueCents, currency)}</b>
      </Block>

      <BlockTitle>{t.method}</BlockTitle>
      <List dividers strongIos insetIos>
        <ListItem
          radio
          radioIcon="end"
          name="pay-method"
          title={t.cash}
          checked={payMethod === 'CASH'}
          onChange={() => setPayMethod('CASH')}
        >
          <i slot="media" className="f7-icons to-bill-nav-ic">
            money_dollar_circle
          </i>
        </ListItem>
        <ListItem
          radio
          radioIcon="end"
          name="pay-method"
          title={t.cardTerminal}
          checked={payMethod === 'MANUAL'}
          onChange={() => setPayMethod('MANUAL')}
        >
          <i slot="media" className="f7-icons to-bill-nav-ic">
            creditcard
          </i>
        </ListItem>
      </List>

      <List dividers strongIos insetIos>
        {/* Amount is editable only for an unsplit tab - a share's amount is set
            by the split and must not drift. */}
        {!share && (
          <ListInput
            type="number"
            label={t.amount}
            placeholder="0.00"
            value={payAmount}
            onInput={(e) => setPayAmount((e.target as HTMLInputElement).value)}
          />
        )}
        <ListInput
          type="number"
          label={t.tip}
          placeholder="0.00"
          value={tip}
          onInput={(e) => setTip((e.target as HTMLInputElement).value)}
        />
      </List>

      <Block>
        <Button fill large round disabled={takePayment.isPending} onClick={submitPayment}>
          {share
            ? t.collectAmount(formatMoney(dueCents, currency))
            : t.recordAmountPayment(formatMoney(inputToMinor(payAmount, currency), currency))}
        </Button>
      </Block>
      <div style={{ height: 'calc(16px + var(--f7-safe-area-bottom, 0px))' }} />
    </div>
  );
}
